import { useInfiniteQuery } from '@zg-rentals/react-query5';
import {
  RentalManagerAccountViewZramMultifamilyWebApiHandler_list,
  RentalManagerAccountViewZramSingleFamilyWebApiHandler_list,
} from '@zg-rentals/ts-rental-manager-api-client';
import {
  useRentalManagerAccountViewZramMultifamilyWebApiHandler_advertising,
  useRentalManagerAccountViewZramMultifamilyWebApiHandler_listingQualityScore,
  useRentalManagerAccountViewZramMultifamilyWebApiHandler_options,
  useRentalManagerAccountViewZramMultifamilyWebApiHandler_prospects,
  useRentalManagerAccountViewZramMultifamilyWebApiHandler_sources,
  useRentalManagerAccountViewZramMultifamilyWebApiHandler_status,
  useRentalManagerAccountViewZramSingleFamilyV2WebApiHandler_advertising,
  useRentalManagerAccountViewZramSingleFamilyWebApiHandler_options,
  useRentalManagerAccountViewZramSingleFamilyWebApiHandler_prospects,
  useRentalManagerAccountViewZramSingleFamilyWebApiHandler_sources,
  useRentalManagerAccountViewZramSingleFamilyWebApiHandler_status,
  useRentalManagerUserPropertySpotlightWebApiHandler_overview,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { transformFiltersToApiParams } from '../../../utils/propertiesPageUtils';

import { useAuth } from '../../../components/AuthProvider';

import type { AccountViewListingsDto } from '@zg-rentals/ts-rental-manager-api-client';
import type { FilterState } from '../../../utils/propertiesPageUtils';

// Re-export for convenience
export { transformFiltersToApiParams };

/**
 * Hook to fetch properties data for enterprise (ZRAM) customers.
 *
 * Automatically routes to single-family or multifamily API based on user type.
 * Supports infinite scroll pagination.
 */
export const useGetPropertiesData = (filters: FilterState, options?: { enabled?: boolean }) => {
  const { user } = useAuth();

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isPending, isError, isFetching } = useInfiniteQuery<
    AccountViewListingsDto,
    Error,
    { pages: Array<AccountViewListingsDto> },
    Array<string | FilterState>,
    string
  >({
    queryKey: ['getProperties', filters],
    enabled: options?.enabled ?? true,
    queryFn: ({ pageParam }) => {
      const apiParams = transformFiltersToApiParams(filters);
      if (user?.isSingleFamilyEnterpriseCustomer) {
        return RentalManagerAccountViewZramSingleFamilyWebApiHandler_list({
          ...apiParams,
          startKeyExclusive: pageParam,
        });
      }
      const { includeSpotlightedOnly: _includeSpotlightedOnly, ...multifamilyParams } = apiParams;
      return RentalManagerAccountViewZramMultifamilyWebApiHandler_list({
        ...multifamilyParams,
        startKeyExclusive: pageParam,
      });
    },
    initialPageParam: '',
    getNextPageParam: (lastPage) => (lastPage.pager?.hasMore ? lastPage.pager.endKey : undefined),
    // @ts-expect-error - returning undefined as it would be during pending
    placeholderData: (previousData) => {
      if (!previousData?.pages?.[0]?.listingCount) {
        return;
      }

      return {
        pages: [
          {
            listingCount: previousData.pages[0].listingCount,
          },
        ],
        pageParams: previousData.pageParams,
      };
    },
    meta: {
      tags: ['getProperties'],
    },
  });

  const propertiesData = data?.pages.length
    ? {
        listings: data.pages.flatMap((page) => page.listings),
        listingCount: data.pages[0].listingCount,
        pager: data.pages[data.pages.length - 1].pager,
      }
    : {};

  return {
    data: propertiesData,
    isPending,
    isFetching,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
};

export const useZramStatus = (params: { listingAlias: string }, options?: { enabled?: boolean }) => {
  const { user } = useAuth();

  const multifamilyResult = useRentalManagerAccountViewZramMultifamilyWebApiHandler_status(params, {
    ...options,
    enabled:
      options?.enabled !== undefined
        ? options.enabled && !user?.isSingleFamilyEnterpriseCustomer
        : !user?.isSingleFamilyEnterpriseCustomer,
  });

  const singleFamilyResult = useRentalManagerAccountViewZramSingleFamilyWebApiHandler_status(params, {
    ...options,
    enabled:
      options?.enabled !== undefined
        ? options.enabled && user?.isSingleFamilyEnterpriseCustomer
        : user?.isSingleFamilyEnterpriseCustomer,
  });

  return user?.isSingleFamilyEnterpriseCustomer ? singleFamilyResult : multifamilyResult;
};

export const useZramListingQualityScore = (params: { listingAlias: string }, options?: { enabled?: boolean }) => {
  const { user } = useAuth();

  // Skip listing quality score completely for single family enterprise users
  return useRentalManagerAccountViewZramMultifamilyWebApiHandler_listingQualityScore(params, {
    ...options,
    enabled:
      options?.enabled !== undefined
        ? options.enabled && !user?.isSingleFamilyEnterpriseCustomer
        : !user?.isSingleFamilyEnterpriseCustomer,
  });
};

export const useZramProspects = (params: { listingAlias: string }, options?: { enabled?: boolean }) => {
  const { user } = useAuth();

  const multifamilyResult = useRentalManagerAccountViewZramMultifamilyWebApiHandler_prospects(params, {
    ...options,
    enabled:
      options?.enabled !== undefined
        ? options.enabled && !user?.isSingleFamilyEnterpriseCustomer
        : !user?.isSingleFamilyEnterpriseCustomer,
  });

  const singleFamilyResult = useRentalManagerAccountViewZramSingleFamilyWebApiHandler_prospects(params, {
    ...options,
    enabled:
      options?.enabled !== undefined
        ? options.enabled && user?.isSingleFamilyEnterpriseCustomer
        : user?.isSingleFamilyEnterpriseCustomer,
  });

  return user?.isSingleFamilyEnterpriseCustomer ? singleFamilyResult : multifamilyResult;
};

export const useZramAdvertising = (params: { listingAlias: string }, options?: { enabled?: boolean }) => {
  const { user } = useAuth();

  // Shares the 'getProperties' tag so spotlight mutations refresh every row's Advertising cell, not just theirs.
  const multifamilyResult = useRentalManagerAccountViewZramMultifamilyWebApiHandler_advertising(params, {
    ...options,
    enabled:
      options?.enabled !== undefined
        ? options.enabled && !user?.isSingleFamilyEnterpriseCustomer
        : !user?.isSingleFamilyEnterpriseCustomer,
    meta: { tags: ['getProperties'] },
  });

  const singleFamilyResult = useRentalManagerAccountViewZramSingleFamilyV2WebApiHandler_advertising(params, {
    ...options,
    enabled:
      options?.enabled !== undefined
        ? options.enabled && user?.isSingleFamilyEnterpriseCustomer
        : user?.isSingleFamilyEnterpriseCustomer,
    meta: { tags: ['getProperties'] },
  });

  return user?.isSingleFamilyEnterpriseCustomer ? singleFamilyResult : multifamilyResult;
};

export const useZramSources = (params: { listingAlias: string }, options?: { enabled?: boolean }) => {
  const { user } = useAuth();

  const multifamilyResult = useRentalManagerAccountViewZramMultifamilyWebApiHandler_sources(params, {
    ...options,
    enabled:
      options?.enabled !== undefined
        ? options.enabled && !user?.isSingleFamilyEnterpriseCustomer
        : !user?.isSingleFamilyEnterpriseCustomer,
  });

  const singleFamilyResult = useRentalManagerAccountViewZramSingleFamilyWebApiHandler_sources(params, {
    ...options,
    enabled:
      options?.enabled !== undefined
        ? options.enabled && user?.isSingleFamilyEnterpriseCustomer
        : user?.isSingleFamilyEnterpriseCustomer,
  });

  return user?.isSingleFamilyEnterpriseCustomer ? singleFamilyResult : multifamilyResult;
};

export const useZramOptions = (params: { listingAlias: string }, options?: { enabled?: boolean }) => {
  const { user } = useAuth();

  const multifamilyResult = useRentalManagerAccountViewZramMultifamilyWebApiHandler_options(params, {
    ...options,
    enabled:
      options?.enabled !== undefined
        ? options.enabled && !user?.isSingleFamilyEnterpriseCustomer
        : !user?.isSingleFamilyEnterpriseCustomer,
    meta: {
      tags: ['zramOptions', params.listingAlias],
      context: {
        api: 'RentalManagerAccountViewZramMultifamilyWebApiHandler_options',
        details: 'useZramOptions',
      },
    },
  });

  const singleFamilyResult = useRentalManagerAccountViewZramSingleFamilyWebApiHandler_options(params, {
    ...options,
    enabled:
      options?.enabled !== undefined
        ? options.enabled && user?.isSingleFamilyEnterpriseCustomer
        : user?.isSingleFamilyEnterpriseCustomer,
    meta: {
      tags: ['zramOptions', params.listingAlias],
      context: {
        api: 'RentalManagerAccountViewZramSingleFamilyWebApiHandler_options',
        details: 'useZramOptions',
      },
    },
  });

  return user?.isSingleFamilyEnterpriseCustomer ? singleFamilyResult : multifamilyResult;
};

export const useSpotlightOverview = () => {
  return useRentalManagerUserPropertySpotlightWebApiHandler_overview({
    meta: {
      tags: ['spotlightOverview'],
      context: { api: 'spotlightOverview', details: 'spotlight overview totals' },
    },
  });
};
